import React, { useMemo } from 'react';
import { Combobox, Flex, TextV2 } from '@withjoy/joykit';
import { Command } from '@withjoy/joykit/components/Command';
import type { TimeZonePickerV2Props } from './TimeZonePickerV2.types';
import { useTranslation } from '@shared/core';
import { useTimezoneOptions } from './useTimezoneOptions.internal';

const TimezoneListItem: React.FC<Readonly<{ label: string; gmtOffset: string }>> = ({ label, gmtOffset }) => (
  <Flex
    justifyContent="space-between"
    width="100%"
    alignItems="center"
  >
    <TextV2
      flex="1"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {label}
    </TextV2>
    <TextV2
      color="mono7"
      marginLeft={3}
      flexShrink={0}
    >
      {gmtOffset}
    </TextV2>
  </Flex>
);

const TimezoneCardItem: React.FC<Readonly<{ city: string; gmtOffset: string }>> = ({ city, gmtOffset }) => (
  <Flex
    flexDirection={{ _: 'row', sm: 'column' }}
    width="100%"
    justifyContent="space-between"
    alignItems={{ _: 'center', sm: 'flex-start' }}
    gap={0}
  >
    <TextV2
      typographyVariant="button1"
      color="mono14"
    >
      {city}
    </TextV2>
    <TextV2
      typographyVariant="label1"
      color="mono10"
    >
      {gmtOffset}
    </TextV2>
  </Flex>
);

export const TimeZonePickerV2: React.FC<TimeZonePickerV2Props> = ({ value, onChange, disabled, placeholder, mode }) => {
  const { tq } = useTranslation('timeZonePickerV2');
  const { allTimezones, popularTimezones } = useTimezoneOptions();

  const displayValue = useMemo(() => {
    if (!value) return '';
    const match = allTimezones.find(tz => tz.value === value) ?? popularTimezones.find(tz => tz.value === value);
    return match ? `${match.gmtOffset} ${match.city}` : '';
  }, [value, allTimezones, popularTimezones]);

  const isButtonMode = mode === 'button';

  const { popularItems, allItems } = useMemo(() => {
    const renderTimezoneItem = (tz: (typeof allTimezones)[number]) => (
      <Command.Item
        key={tz.value}
        value={tz.value}
        keywords={[tz.city, tz.label, tz.gmtOffset, tz.value]}
      >
        {isButtonMode ? (
          <TimezoneCardItem
            city={tz.city}
            gmtOffset={tz.gmtOffset}
          />
        ) : (
          <TimezoneListItem
            label={tz.label}
            gmtOffset={tz.gmtOffset}
          />
        )}
      </Command.Item>
    );

    return {
      popularItems: popularTimezones.map(renderTimezoneItem),
      allItems: allTimezones.map(renderTimezoneItem),
    };
  }, [allTimezones, popularTimezones, isButtonMode]);

  const comboboxProps = {
    value,
    onValueChange: onChange,
    disabled,
    placeholder: placeholder || tq`Select timezone...`,
    searchPlaceholder: tq`Search timezones...`,
    emptyMessage: tq`No timezones found`,
    displayValue,
  };

  if (isButtonMode) {
    // Button mode: flat list of all timezones (no groups)
    const allButtonItems = [...popularItems, ...allItems];
    return (
      <Combobox
        mode="button"
        buttonProps={{ intent: 'neutral', shape: 'rounded' }}
        dialogTitle={tq`Choose Timezone`}
        {...comboboxProps}
      >
        {allButtonItems}
      </Combobox>
    );
  }

  const timezoneContent = (
    <>
      <Command.Group heading={tq`Popular Timezones`}>{popularItems}</Command.Group>
      <Command.Group heading={tq`All Timezones`}>{allItems}</Command.Group>
    </>
  );

  return <Combobox {...comboboxProps}>{timezoneContent}</Combobox>;
};

TimeZonePickerV2.displayName = 'TimeZonePickerV2';
